import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, Loader2, RefreshCw, Send, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export function SocialCaption({ tid, matchId, compact = false }) {
  const [data, setData] = useState(null);
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState("");
  const [post, setPost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [err, setErr] = useState("");
  const load = async (regenerate = false) => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/ai/tournaments/${tid}/matches/${matchId}/caption`, { params: regenerate ? { regenerate: true } : {} });
      setData(r.data); setCaption(r.data.caption || ""); setTags((r.data.hashtags || []).join(" "));
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (!matchId) return;
    setData(null); setPost(null); load();
    api.get(`/tournaments/${tid}/matches/${matchId}/caption-post`).then((r) => setPost(r.data || null)).catch(() => {});
  }, [tid, matchId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!matchId) return null;
  const hashtags = tags.split(/\s+/).filter(Boolean).map((x) => (x.startsWith("#") ? x : `#${x}`));
  const full = `${caption}\n\n${hashtags.join(" ")}`;
  const publish = async () => {
    setPublishing(true);
    try {
      const { data: p } = await api.post(`/tournaments/${tid}/matches/${matchId}/caption-post`, { caption, hashtags });
      setPost(p);
      toast.success(p.updated ? "Post aggiornato sul sito del torneo" : "Post pubblicato sul sito del torneo");
    } catch (e) { toast.error(apiError(e)); } finally { setPublishing(false); }
  };
  return (
    <div className={`rounded-lg border border-white/10 bg-ink-950/50 ${compact ? "p-3" : "p-4"} space-y-2`} data-testid="social-caption">
      <div className="flex items-center gap-2 text-xs font-display font-extrabold uppercase text-fsl-gold"><Share2 className="h-4 w-4" /> Didascalia social (IA)<span className="ml-auto text-[10px] text-fsl-slate font-sans font-normal normal-case tracking-normal">{data?.generated_at ? `generata ${new Date(data.generated_at).toLocaleString("it-IT")}` : ""}</span></div>
      {busy && !data ? <div className="text-xs text-fsl-slate inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Scrivo la didascalia…</div> : err ? <p className="text-xs text-fsl-slate" data-testid="social-caption-error">{err}</p> : data && (
        <>
          <textarea className="fsl-input w-full min-h-[120px] text-sm leading-relaxed py-2" value={caption} onChange={(e) => setCaption(e.target.value)} aria-label="Testo del post" data-testid="social-caption-text" />
          <input className="fsl-input h-9 w-full text-xs text-fsl-gold" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="#hashtag separati da spazio" aria-label="Hashtag" data-testid="social-caption-tags" />
          <p className="text-[11px] text-fsl-slate">Modifica liberamente testo e hashtag: il post viene pubblicato nelle news del torneo collegato a gara, squadre e società.</p>
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-gold h-8 px-3 text-[11px]" disabled={!data || publishing || caption.trim().length < 10} onClick={publish} data-testid="social-caption-publish">{publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {post ? "Aggiorna post" : "Pubblica sul sito"}</button>
        <button type="button" className="btn-ghost h-8 px-3 text-[11px]" disabled={!data} onClick={() => { navigator.clipboard?.writeText(full); toast.success("Didascalia e hashtag copiati"); }} data-testid="social-caption-copy"><Copy className="h-3.5 w-3.5" /> Copia</button>
        <button type="button" className="btn-ghost h-8 px-3 text-[11px]" disabled={busy} onClick={() => load(true)} data-testid="social-caption-regen"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Rigenera</button>
        {post?.public_url && <Link to={post.public_url} target="_blank" className="ml-auto inline-flex items-center gap-1 text-[11px] text-fsl-success hover:underline" data-testid="social-caption-published-link"><Check className="h-3.5 w-3.5" /> Pubblicato · apri <ExternalLink className="h-3 w-3" /></Link>}
      </div>
    </div>
  );
}
